// useFilterManager.js
import { useState, useMemo, useCallback } from 'react';
import { useEnumsApi } from './useEnumsApi';
import { filterService } from '../services/filterService';
import { FILTER_CONFIG } from '../components/filters/FilterConfig';

/**
 * useFilterManager V2.3 - Manual Trigger + Additional Filter Support
 * Manages "Draft" state for UI and "Applied" state for API calls.
 * 
 * Supports both standard Maintenance Filters (Array) and 
 * Additional Filters (Flat Object for Dates/Text/etc).
 */
export function useFilterManager(initialKeys = []) {
  const { maintenanceTypes, loading: loadingEnums } = useEnumsApi();

  const emptyState = initialKeys.reduce((acc, key) => ({ ...acc, [key]: null }), {});

  // 1. Draft State: Updates instantly when user clicks a dropdown
  const [draftFilters, setDraftFilters] = useState(emptyState);

  // 2. Applied State: Only updates when "Generate" is clicked
  const [appliedFilters, setAppliedFilters] = useState(emptyState);

  // Updates the UI (Draft) only
  // Recursive/Waterfall Reset Logic
  const onFilterChange = useCallback((key, value) => {
    setDraftFilters((prev) => {
      // 1. Update the immediate field
      const updated = {
        ...prev,
        [key]: value
      };

      // 2. Waterfall Reset: Loop until no more dependent changes are found
      let stateChanged = true;
      while (stateChanged) {
        stateChanged = false;
        initialKeys.forEach((otherKey) => {
          const config = FILTER_CONFIG[otherKey];
          if (!config) return;

          const currentValue = updated[otherKey];
          if (currentValue === null) return; // Already empty

          // Rule A: Clear if any parent is null
          const parentIsMissing = config.dependsOn?.some(depKey => updated[depKey] === null);

          // Rule B: Clear if the field's own visibility rule now returns false
          const isHiddenByRule = config.isVisible && !config.isVisible(updated);

          if (parentIsMissing || isHiddenByRule) {
            updated[otherKey] = null; // REPLACED: Clears the stale state
            stateChanged = true; // Trigger another pass to catch grandchildren
          }
        });
      }

      return updated;
    });
  }, [initialKeys]);

  const applyFilters = useCallback(() => {
    setAppliedFilters(draftFilters);
  }, [draftFilters]);

  // Resets everything
  const resetFilters = useCallback(() => {
    setDraftFilters(emptyState);
    setAppliedFilters(emptyState);
  }, [emptyState]);

  /**
   * MODIFIED: Split Payload Logic
   * 1. apiFilterPayload: Handles the legacy Maintenance Array OR the Payroll DTO
   * 2. additionalApiFilterPayload: Handles flat data (Dates, Static Selects, etc), // only use this if will add additional filter that is not a maintenance
   */
  const { apiFilterPayload, additionalApiFilterPayload } = useMemo(() => {


    // converting the applied filters to use the correct object name to be mapped
    const filtersForDetails = Object.fromEntries(
      initialKeys.map((filterKey) => {
        const config = FILTER_CONFIG[filterKey];

        return [
          config?.dropdownTypeKey ?? filterKey,
          appliedFilters[filterKey]
        ];
      })
    );

    const baseFilters = filterService.prepareMaintenancePayload(filtersForDetails, maintenanceTypes);

    // B. Get the additional filters (the ones marked as ADDITIONAL in FilterConfig)
    const additionalFilters = filterService.prepareAdditionalPayload(appliedFilters);

    // C. Preserve your Payroll Module Logic
    const isPayrollModule = 'PAYROLLPERIOD' in appliedFilters;

    let mainPayload;

    if (isPayrollModule) {
      // Return the specific Payroll DTO structure
      mainPayload = {
        PayrollPeriodId: appliedFilters.PAYROLLPERIOD?.id || 0,
        PayTypeId: appliedFilters.PAYTYPE?.id || 0,
        ClassificationId: appliedFilters.CLASSIFICATION?.id || 0,
        // Map keys to PascalCase (Type/Value) to match C# DTO perfectly
        Filters: baseFilters.map(f => ({
          Type: f.type,
          Value: f.value
        }))
      };
    } else {
      // Return standard array for non-payroll modules
      mainPayload = baseFilters;
    }

    return {
      apiFilterPayload: mainPayload,
      additionalApiFilterPayload: additionalFilters
    };
  }, [appliedFilters, maintenanceTypes]);

  /**
   * Helper to get the current UI filters formatted as 
   * Salary Rule Details for auto-populating modals.
   * MODIFIED: Added visibility check to prevent stale data from populating modals
   */
  const getFiltersAsDetails = useCallback(() => {
    const filteredState = {};

    initialKeys.forEach((filterKey) => {
      const config = FILTER_CONFIG[filterKey];
      const value = draftFilters[filterKey];

      // Check if the filter should be visible based on current state
      const isVisible = config?.isVisible ? config.isVisible(draftFilters) : true;

      // Only export if it has a value AND is currently visible
      if (value && isVisible) {
        const apiKey = config?.dropdownTypeKey ?? filterKey;
        filteredState[apiKey] = value;
      }
    });

    return filterService.mapFiltersToRuleDetails(
      filteredState,
      maintenanceTypes
    );
  }, [draftFilters, maintenanceTypes, initialKeys]);


  /**
   * Extracts and formats non-maintenance (additional) draft filters 
   * for auto-populating modals or dynamic components.
   */
  const getOtherFilterAsDetails = useCallback(() => {
    // 1. Run prepareAdditionalPayload on draft state (same logic as additionalFilters)
    const rawAdditionalFilters = filterService.prepareAdditionalPayload(draftFilters) || {};

    // 2. Filter out any items that are currently hidden by visibility rules
    const visibleDetails = {};

    Object.entries(rawAdditionalFilters).forEach(([key, value]) => {
      // Match key back to initialKeys/FILTER_CONFIG (handling fallback keys)
      const filterKey = initialKeys.find((k) => {
        const config = FILTER_CONFIG[k];
        return (config?.dropdownTypeKey ?? k) === key || k === key;
      });

      const config = filterKey ? FILTER_CONFIG[filterKey] : null;
      const isVisible = config?.isVisible ? config.isVisible(draftFilters) : true;

      if (isVisible) {
        visibleDetails[key] = value;
      }
    });

    return visibleDetails;
  }, [draftFilters, initialKeys]);

  return {
    activeFilterState: draftFilters, // Linked to UI components
    loadingEnums,
    onFilterChange,
    apiFilterPayload,               // Standard/Payroll Payload
    additionalApiFilterPayload,      // ADDED: Flat Object for new filters
    applyFilters,                   // Linked to "Generate" button
    resetFilters,                   // Linked to "Clear" button
    getFiltersAsDetails,
    getOtherFilterAsDetails,
  };
}